import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/Dialog";
import classNames from "components/ui/classNames";
import { CopyIcon, ShareIcon } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useIntl } from "react-intl";
interface ShareThisPageDialogProps {
  title?: string;
  description?: string;
}
export default function ShareThisPageDialog({ title, description }: ShareThisPageDialogProps) {
  const intl = useIntl();

  const [isOpen, setIsOpen] = useState(false);

  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, [isOpen]);
  const urlToShare = currentUrl ? new URL(currentUrl).toString() : "";

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild={true}>
        <button
          type="button"
          className={classNames(
            "hidden lg:flex",
            "rounded-xl bg-slate-50/60 p-2",
            "border border-slate-200 text-sm font-medium tracking-wide text-slate-600",
            "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
            "focus-visible:outline-none",
            "focus-visible:ring-1 focus-visible:ring-offset-1",
            "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
            "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
            "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
            "hover:bg-slate-100 hover:dark:bg-slate-600",
          )}
        >
          <ShareIcon />
        </button>
      </DialogTrigger>

      <DialogContent
        className="rounded-none border-slate-300 bg-white sm:rounded-3xl"
        onOpenAutoFocus={(event) => {
          // See: https://github.com/radix-ui/primitives/discussions/935
          event.preventDefault();
        }}
      >
        <DialogHeader className="space-y-1 text-left">
          <DialogTitle>
            {title ||
              intl.formatMessage({
                defaultMessage: "Compartilhar",
                id: "g/TS4a",
                description: "Share dialog title",
              })}
          </DialogTitle>
          <DialogDescription className="text-sm italic tracking-wide text-slate-500">
            {description ||
              intl.formatMessage({
                defaultMessage: "Compartilhe essa visualização com outras pessoas...",
                id: "t2Fo7T",
                description: "Search dialog description",
              })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <input
            type="text"
            readOnly={true}
            value={addUtmTags(urlToShare, {
              source: "fundamentei",
              medium: "share",
            })}
            onClick={(event) => {
              const urlToShare = addUtmTags(window.location.href, {
                source: "fundamentei",
                medium: "share",
              });
              if (urlToShare) {
                event.currentTarget.select();
                event.currentTarget.focus();
                try {
                  navigator.clipboard.writeText(urlToShare);
                  toast.success(
                    intl.formatMessage({
                      id: "qDxsv5",
                      defaultMessage: "Link copiado!",
                    }),
                  );
                } catch {
                  /* empty */
                }
              }
            }}
            className={classNames(
              "group inline-flex w-full gap-x-4 truncate text-ellipsis",
              "relative h-10 items-center bg-white px-3 text-sm font-medium italic text-blue-600",
              "rounded-xl",
              "cursor-text justify-between tracking-wider",
              "border border-transparent",
              "bg-slate-100 hover:bg-slate-200/70 dark:hover:bg-slate-950 dark:hover:text-white",
              "dark:ring-slate-950",
              "dark:border-slate-950 dark:bg-slate-900 dark:text-slate-300",
              "focus-visible:outline-none",
              "focus-visible:ring-1 focus-visible:ring-offset-1",
              "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
              "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
              "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
              "disabled:pointer-events-none disabled:cursor-not-allowed",
            )}
          />

          {urlToShare && (
            <button
              type="button"
              onClick={() => {
                const finalUrlToShare = addUtmTags(urlToShare, {
                  source: "fundamentei",
                  medium: "share",
                });
                if (finalUrlToShare) {
                  try {
                    navigator.clipboard.writeText(finalUrlToShare);
                    toast.success(
                      intl.formatMessage({
                        id: "qDxsv5",
                        defaultMessage: "Link copiado!",
                      }),
                    );
                  } catch {
                    /* empty */
                  }
                }
              }}
              className={classNames(
                "rounded-xl bg-slate-50 p-2",
                "text-sm font-medium tracking-wide text-slate-600",
                "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
                "focus-visible:outline-none",
                "focus-visible:ring-1 focus-visible:ring-offset-1",
                "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
                "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
                "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
                "hover:bg-slate-100 hover:dark:bg-slate-600",
              )}
            >
              <CopyIcon />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

type UTM = {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
};
function addUtmTags(url: string, tags: UTM = {}) {
  if (!url) {
    return "";
  }
  const tagged = new URL(url);
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(tags)) {
    tagged.searchParams.append(`utm_${key}`, value);
  }
  return tagged.toString();
}
